import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Sale } from '../types';
import { salesService, SaleFilters } from '../services/salesService';
import ConfirmationModal from '../components/ConfirmationModal';
import ReturnRequestModal from '../components/ReturnRequestModal';
import { 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  FileText,
  X,
  Check,
  Grid,
  List,
  Filter,
  Calendar,
  CreditCard,
  Package,
  User,
  DollarSign,
  CheckSquare,
  Square,
  RotateCcw
} from 'lucide-react';
import { customToast } from '../utils/customToast';
import { useTitle } from '../hooks/useTitle';
import { useCurrency } from '../hooks/useCurrency';

type ViewMode = 'grid' | 'table';

const SalesManagementPage: React.FC = () => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [searchParams] = useSearchParams();
  
  // Set page title
  useTitle('Sales Management');

  // State management
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [selectedSales, setSelectedSales] = useState<number[]>([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [isClosingViewModal, setIsClosingViewModal] = useState(false);
  const [isClosingEditModal, setIsClosingEditModal] = useState(false);
  const [viewModalLoading, setViewModalLoading] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [showFilters, setShowFilters] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    customer_id: null as number | null,
    customer_name: '',
    paid_amount: 0,
    payment_method: 'cash' as 'cash' | 'bank_transfer' | 'other',
    payment_status: 'paid' as 'paid' | 'partial' | 'due',
    notes: ''
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    payment_status: 'all',
    payment_method: 'all',
    date_from: '',
    date_to: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger'
  });

  // Refresh the currently viewed sale details
  const refreshSelectedSale = useCallback(async () => {
    if (!selectedSale?.id) return;
    
    try {
      const updatedSale = await salesService.getSale(selectedSale.id);
      console.log('Updated sale from API:', updatedSale);
      console.log('ProductReturns in API response:', (updatedSale as any).product_returns || (updatedSale as any).productReturns);
      
      const saleData = {
        ...updatedSale,
        saleItems: (updatedSale as any).sale_items || (updatedSale as any).saleItems || (updatedSale as any).items || [],
        productReturns: (updatedSale as any).product_returns || (updatedSale as any).productReturns || []
      };
      console.log('Setting selectedSale with data:', saleData);
      setSelectedSale(saleData);
    } catch (error) {
      console.error('Failed to refresh sale details:', error);
    }
  }, [selectedSale?.id]);

  // Load sales data
  const loadSales = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading sales with filters:', filters);
      
      const salesFilters: SaleFilters = {
        search: filters.search || undefined,
        payment_status: filters.payment_status !== 'all' ? (filters.payment_status as 'paid' | 'partial' | 'due') : undefined,
        payment_method: filters.payment_method !== 'all' ? (filters.payment_method as any) : undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        sortBy: filters.sortBy as 'sale_date' | 'total_amount' | 'customer_name',
        sortOrder: filters.sortOrder as 'asc' | 'desc'
      };
      
      console.log('Sending filters to API:', salesFilters);
      console.log('Payment method filter value:', filters.payment_method, 'Is all?', filters.payment_method === 'all');
      const response = await salesService.getSales(salesFilters, 1, 50);
      console.log('Sales response:', response);
      setSales(response.data);
    } catch (error: any) {
      console.error('Failed to load sales:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = 'Failed to load sales';
      if (error.response?.status === 401) {
        errorMessage = 'Please login to view sales data';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      customToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Handle return success - reload sales and refresh current sale
  const handleReturnSuccess = useCallback(async () => {
    await loadSales();
    await refreshSelectedSale();
  }, [loadSales, refreshSelectedSale]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  // View sale details
  const viewSale = async (sale: Sale) => {
    // Open modal immediately with sale from list
    setSelectedSale(sale);
    setShowViewModal(true);
    setViewModalLoading(true);

    try {
      const response = await salesService.getSale(sale.id);
      console.log('Full sale details response:', JSON.stringify(response, null, 2));
      console.log('Response keys:', Object.keys(response));
      console.log('Has saleItems?', 'saleItems' in response);
      console.log('Has sale_items?', 'sale_items' in response);
      console.log('Has productReturns?', 'productReturns' in response);
      console.log('Has product_returns?', 'product_returns' in response);
      console.log('SaleItems value:', response.saleItems);
      console.log('Sale_items value:', (response as any).sale_items);
      console.log('ProductReturns value:', (response as any).productReturns || (response as any).product_returns);
      console.log('SaleItems type:', typeof response.saleItems);
      
      // Map sale_items to saleItems and product_returns to productReturns for frontend compatibility
      const saleData = {
        ...response,
        saleItems: (response as any).sale_items || response.saleItems || [],
        productReturns: (response as any).product_returns || (response as any).productReturns || []
      };
      
      console.log('Setting selectedSale with:', saleData);
      setSelectedSale(saleData);
    } catch (error) {
      console.error('Failed to load sale details:', error);
      customToast.error('Failed to load sale details');
    } finally {
      setViewModalLoading(false);
    }
  };

  // Helper function to calculate payment status based on paid amount
  const calculatePaymentStatus = (paidAmount: number, totalAmount: number): 'paid' | 'partial' | 'due' => {
    if (paidAmount >= totalAmount) {
      return 'paid';
    } else if (paidAmount > 0 && paidAmount < totalAmount) {
      return 'partial';
    } else {
      return 'due';
    }
  };

  // Edit sale
  const editSale = (sale: Sale) => {
    // Open modal immediately with sale from list
    setSelectedSale(sale);
    setEditModalLoading(true);
    
    const paidAmount = Number(sale.paid_amount);
    const totalAmount = Number(sale.total_amount);
    const calculatedStatus = calculatePaymentStatus(paidAmount, totalAmount);
    
    setEditForm({
      customer_id: sale.customer_id || null,
      customer_name: sale.customer_display_name || sale.customer?.name || '',
      paid_amount: paidAmount,
      payment_method: sale.payment_method,
      payment_status: calculatedStatus,
      notes: sale.notes || ''
    });
    setShowEditModal(true);

    // Fetch full sale data in background
    const fetchFullSale = async () => {
      try {
        const response = await salesService.getSale(sale.id);
        const saleData = {
          ...response,
          saleItems: (response as any).sale_items || response.saleItems || []
        };
        setSelectedSale(saleData);
      } catch (error) {
        console.error('Failed to load sale details for edit:', error);
        customToast.error('Failed to load sale details');
      } finally {
        setEditModalLoading(false);
      }
    };

    fetchFullSale();
  };

  const handleReturnSale = async (sale: Sale) => {
    // Fetch full sale details including items
    try {
      const fullSale = await salesService.getSale(sale.id);
      console.log('Full sale for return:', fullSale);
      
      // Map sale_items to saleItems for consistency
      const saleData = {
        ...fullSale,
        saleItems: (fullSale as any).sale_items || (fullSale as any).saleItems || (fullSale as any).items || []
      };
      
      console.log('Sale data after mapping:', saleData);
      setSelectedSale(saleData);
      setShowReturnModal(true);
    } catch (error) {
      customToast.error('Failed to load sale details');
    }
  };

  // Update sale
  const updateSale = async () => {
    if (!selectedSale) return;

    try {
      await salesService.updateSale(selectedSale.id, editForm);
      customToast.success('Sale updated successfully');
      handleCloseEditModal();
      loadSales();
    } catch (error) {
      console.error('Failed to update sale:', error);
      customToast.error('Failed to update sale');
    }
  };

  // Delete sale
  const deleteSale = async (saleId: number, invoiceNumber: string) => {
    if (!saleId) {
      console.error('No sale ID provided for deletion');
      customToast.error('No sale selected');
      return;
    }

    console.log('Starting delete for sale:', saleId, invoiceNumber);
    setDeleteLoading(true);
    try {
      console.log('Calling API to delete sale:', saleId);
      const result = await salesService.deleteSale(saleId);
      console.log('Delete API response:', result);
      customToast.success('Sale deleted successfully');
      setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      setSelectedSale(null);
      await loadSales();
      console.log('Sale deleted and list reloaded');
    } catch (error) {
      console.error('Failed to delete sale:', error);
      customToast.error(error instanceof Error ? error.message : 'Failed to delete sale');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteSale = (sale: Sale) => {
    console.log('handleDeleteSale called with:', sale.id, sale.invoice_number);
    setSelectedSale(sale);
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Sale',
      message: `Are you sure you want to delete sale ${sale.invoice_number || `#${sale.id}`}? This action cannot be undone and will restore the product stock.`,
      onConfirm: () => {
        console.log('Confirmation modal onConfirm called');
        deleteSale(sale.id, sale.invoice_number || `Sale #${sale.id}`);
      },
      type: 'danger'
    });
  };

  // Bulk selection functions
  const handleSelectSale = (saleId: number) => {
    setSelectedSales(prev => 
      prev.includes(saleId) 
        ? prev.filter(id => id !== saleId)
        : [...prev, saleId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSales.length === filteredSales.length) {
      setSelectedSales([]);
    } else {
      setSelectedSales(filteredSales.map(sale => sale.id));
    }
  };

  // Bulk delete
  const handleBulkDelete = () => {
    if (selectedSales.length === 0) return;
    
    const idsToDelete = [...selectedSales];
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Selected Sales',
      message: `Are you sure you want to delete ${selectedSales.length} sale(s)? This action cannot be undone and will restore the product stock for all selected sales.`,
      onConfirm: () => {
        console.log('Bulk delete confirmation, deleting:', idsToDelete);
        bulkDeleteSales(idsToDelete);
      },
      type: 'danger'
    });
  };

  const bulkDeleteSales = async (idsToDelete: number[]) => {
    console.log('bulkDeleteSales called with:', idsToDelete);
    setDeleteLoading(true);
    try {
      const result = await salesService.bulkDeleteSales(idsToDelete);
      console.log('Bulk delete response:', result);
      
      if (result && result.deleted_count !== undefined) {
        if (result.deleted_count > 0) {
          customToast.success(`Successfully deleted ${result.deleted_count} sale(s)`);
        } else {
          customToast.info(`No sales were deleted`);
        }
        
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach((error: string) => customToast.error(error));
        }
      } else {
        console.warn('Unexpected response format:', result);
        customToast.warning('Bulk delete completed with unexpected response format');
      }
      
      setSelectedSales([]);
      setConfirmationModal(prev => ({ ...prev, isOpen: false }));
      await loadSales();
      console.log('Bulk delete completed and list reloaded');
    } catch (error) {
      console.error('Failed to bulk delete sales:', error);
      customToast.error(error instanceof Error ? error.message : 'Failed to delete selected sales');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Modal close handlers with animation
  const handleCloseViewModal = () => {
    setIsClosingViewModal(true);
    setTimeout(() => {
      setIsClosingViewModal(false);
      setShowViewModal(false);
    }, 150);
  };

  const handleCloseEditModal = () => {
    setIsClosingEditModal(true);
    setTimeout(() => {
      setIsClosingEditModal(false);
      setShowEditModal(false);
    }, 150);
  };

  // Badge styling functions
  const formatStatusText = (status: string) => {
    return status
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'due':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'partial_return':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'returned':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getPaymentMethodBadge = (method: string) => {
    switch (method) {
      case 'cash':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'card':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'upi':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'credit':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // Helper function to format payment method display text
  const formatPaymentMethod = (method: string) => {
    return method
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // No client-side filtering needed - all filtering is done server-side
  const filteredSales = sales || [];

  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Sales Management
          </h1>
          <p className={`mt-1 text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            View and manage all sales transactions
          </p>
        </div>
        
        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-3">
          {/* View Mode Toggle */}
          <div className={`flex items-center border rounded-xl p-1 transition-all duration-200 ${
            theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'
          }`}>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                viewMode === 'table'
                  ? 'bg-blue-500 text-white shadow-md'
                  : theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title="Table View"
            >
              <List size={16} />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                viewMode === 'grid'
                  ? 'bg-blue-500 text-white shadow-md'
                  : theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title="Grid View"
            >
              <Grid size={16} />
            </button>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 rounded-xl border transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation ${
              theme === 'dark'
                ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700 hover:border-gray-500'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
            }`}
            title={showFilters ? 'Hide Filters' : 'Show Filters'}
          >
            <Filter size={16} />
            <span className="text-sm">Filters</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search size={16} className={`absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`} />
        <input
          type="text"
          placeholder="Search sales..."
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border transition-all duration-200 shadow-sm focus:shadow-md touch-manipulation ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:bg-gray-750'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-gray-50'
          } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20`}
        />
      </div>

      {/* Filters */}
      {showFilters && (
        <div className={`p-4 sm:p-6 rounded-lg border ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Payment Status Filter */}
            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Payment Status
              </label>
              <select
                value={filters.payment_status}
                onChange={(e) => setFilters(prev => ({ ...prev, payment_status: e.target.value }))}
                className={`w-full px-3 py-2.5 sm:py-2 border rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-md touch-manipulation ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300'
                }`}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="due">Due</option>
                <option value="partial_return">Partial Return</option>
                <option value="returned">Returned</option>
              </select>
            </div>

            {/* Payment Method Filter */}
            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Payment Method
              </label>
              <select
                value={filters.payment_method}
                onChange={(e) => setFilters(prev => ({ ...prev, payment_method: e.target.value }))}
                className={`w-full px-3 py-2.5 sm:py-2 border rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-md touch-manipulation ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300'
                }`}
              >
                <option value="all">All Methods</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                From Date
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                className={`w-full px-3 py-2.5 sm:py-2 border rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-md touch-manipulation ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300'
                }`}
              />
            </div>

            {/* Date To */}
            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                To Date
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                className={`w-full px-3 py-2.5 sm:py-2 border rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-md touch-manipulation ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sales Display */}
      {selectedSales.length > 0 && (
        <div className={`bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 sm:p-4 mb-4 border border-blue-200 dark:border-blue-700`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center">
                <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2" />
                <span className={`text-sm sm:text-base font-medium ${theme === 'dark' ? 'text-blue-300' : 'text-blue-800'}`}>
                  {selectedSales.length} sale(s) selected
                </span>
              </div>
              <div className="flex space-x-2 w-full sm:w-auto">
                <button
                  onClick={() => setSelectedSales([])}
                  className={`flex-1 sm:flex-none px-3 py-2 rounded-lg text-sm font-medium transition-colors touch-manipulation ${
                    theme === 'dark' 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Clear Selection
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="flex-1 sm:flex-none px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center touch-manipulation"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Selected
                </button>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'table' ? (
          <div className={`bg-white ${theme === 'dark' ? 'dark:bg-gray-800' : ''} rounded-lg overflow-hidden`}>
            {/* Mobile Card View */}
            <div className="block sm:hidden">
              {loading ? (
                <div className="space-y-3 p-4">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border animate-pulse ${
                        theme === 'dark'
                          ? 'bg-slate-700 border-slate-600'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {/* Header: Checkbox + Sale ID + Invoice */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3">
                          {/* Checkbox skeleton */}
                          <div className={`w-5 h-5 rounded flex-shrink-0 mt-1 ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          <div className="flex-1">
                            {/* Sale ID skeleton */}
                            <div className={`h-5 w-24 rounded mb-1 ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                            {/* Invoice skeleton */}
                            <div className={`h-3 w-20 rounded ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                          </div>
                        </div>
                        {/* Status badge skeleton */}
                        <div className={`h-5 w-16 rounded-full ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>

                      {/* Customer & Date grid */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          {/* Customer label skeleton */}
                          <div className={`h-3 w-16 rounded mb-1 ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          {/* Customer value skeleton */}
                          <div className={`h-4 w-20 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                        <div>
                          {/* Date label skeleton */}
                          <div className={`h-3 w-12 rounded mb-1 ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          {/* Date value skeleton */}
                          <div className={`h-4 w-24 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                      </div>

                      {/* Amount & Payment grid */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          {/* Amount label skeleton */}
                          <div className={`h-3 w-16 rounded mb-1 ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          {/* Amount value skeleton */}
                          <div className={`h-5 w-24 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                        <div>
                          {/* Payment badge skeleton */}
                          <div className={`h-5 w-20 rounded-full ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                      </div>

                      {/* Action buttons skeleton */}
                      <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className={`h-4 w-12 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className="flex space-x-3">
                          <div className={`h-4 w-12 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          <div className={`h-4 w-16 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredSales.length === 0 ? (
                <div className="flex flex-col items-center py-12">
                  <FileText className={`w-12 h-12 mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    No sales found
                  </p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {filters.search ? 'Try adjusting your search or filters' : 'No sales data available'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredSales.map((sale) => (
                    <div key={sale.id} className={`p-4 hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} transition-colors ${
                      selectedSales.includes(sale.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3">
                          <button
                            onClick={() => handleSelectSale(sale.id)}
                            className="flex items-center mt-1 touch-manipulation"
                          >
                            {selectedSales.includes(sale.id) ? (
                              <CheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                          <div>
                            <div className={`text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              Sale #{sale.shop_display_id || sale.id}
                            </div>
                            {sale.invoice_number && (
                              <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                {sale.invoice_number}
                              </div>
                            )}
                          </div>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          getPaymentStatusBadge((sale as any).display_status || sale.payment_status)
                        }`}>
                          {formatStatusText((sale as any).display_status || sale.payment_status)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Customer</div>
                          <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {sale.customer?.name || sale.customer_name || 'Walk-in Customer'}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Date</div>
                          <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {new Date(sale.created_at || '').toLocaleDateString('en-PK')}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Amount</div>
                          <div className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {formatCurrency(Number(sale.total_amount))}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Payment</div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            getPaymentMethodBadge(sale.payment_method)
                          }`}>
                            {formatPaymentMethod(sale.payment_method)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => viewSale(sale)}
                          className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors touch-manipulation"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => editSale(sale)}
                            className="flex items-center text-amber-600 hover:text-amber-800 text-sm font-medium transition-colors touch-manipulation"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSale(sale)}
                            className="flex items-center text-red-600 hover:text-red-800 text-sm font-medium transition-colors touch-manipulation"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      <button
                        onClick={handleSelectAll}
                        className="flex items-center space-x-1 hover:text-blue-600 transition-colors touch-manipulation"
                      >
                        {selectedSales.length === filteredSales.length && filteredSales.length > 0 ? (
                          <CheckSquare className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Sale ID
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Customer
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Date
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Amount
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Status
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Payment
                    </th>
                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                  {loading ? (
                    <>
                      {Array.from({ length: 5 }).map((_, rowIndex) => (
                        <tr key={rowIndex} className="animate-pulse hover:bg-opacity-50">
                          {/* Checkbox skeleton */}
                          <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                            <div className={`w-5 h-5 rounded ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                          </td>
                          
                          {/* Sale ID skeleton (2 lines: ID + Invoice) */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`h-4 w-20 rounded mb-1 ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                            <div className={`h-3 w-24 rounded ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                          </td>
                          
                          {/* Customer skeleton (Icon + Name) */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`w-4 h-4 rounded mr-2 flex-shrink-0 ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              <div className={`h-4 w-28 rounded ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                            </div>
                          </td>
                          
                          {/* Date skeleton (Icon + Date) */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`w-4 h-4 rounded mr-2 flex-shrink-0 ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              <div className={`h-4 w-24 rounded ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                            </div>
                          </td>
                          
                          {/* Amount skeleton (Icon + Amount) */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`w-4 h-4 rounded mr-1 flex-shrink-0 ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              <div className={`h-4 w-20 rounded ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                            </div>
                          </td>
                          
                          {/* Status skeleton */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`h-5 w-16 rounded-full ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                          </td>
                          
                          {/* Payment Method skeleton (Icon + Badge) */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`w-4 h-4 rounded mr-2 flex-shrink-0 ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              <div className={`h-5 w-16 rounded-full ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                            </div>
                          </td>
                          
                          {/* Actions skeleton (3 icons) */}
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <div className={`w-4 h-4 rounded ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              <div className={`w-4 h-4 rounded ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              <div className={`w-4 h-4 rounded ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </>
                  ) : filteredSales.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <FileText className={`w-12 h-12 mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                          <p className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                            No sales found
                          </p>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {filters.search ? 'Try adjusting your search or filters' : 'No sales data available'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredSales.map((sale) => (
                      <tr key={sale.id} className={`hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} transition-colors`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleSelectSale(sale.id)}
                            className="flex items-center space-x-1 hover:text-blue-600 transition-colors"
                          >
                            {selectedSales.includes(sale.id) ? (
                              <CheckSquare className="w-4 h-4 text-blue-600" />
                            ) : (
                              <Square className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            #{sale.shop_display_id || sale.id}
                          </div>
                          {sale.invoice_number && (
                            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              {sale.invoice_number}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-2" />
                            <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {sale.customer?.name || sale.customer_name || 'Walk-in Customer'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                              {new Date(sale.created_at || '').toLocaleDateString('en-PK')}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 text-gray-400 mr-1" />
                            <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {formatCurrency(Number(sale.total_amount))}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            getPaymentStatusBadge((sale as any).display_status || sale.payment_status)
                          }`}>
                            {formatStatusText((sale as any).display_status || sale.payment_status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CreditCard className="w-4 h-4 text-gray-400 mr-2" />
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              getPaymentMethodBadge(sale.payment_method)
                            }`}>
                              {formatPaymentMethod(sale.payment_method)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => viewSale(sale)}
                              className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              className="text-yellow-600 hover:text-yellow-800 transition-colors p-1 rounded"
                              onClick={() => editSale(sale)}
                              title="Edit Sale"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              className="text-orange-600 hover:text-orange-800 transition-colors p-1 rounded"
                              onClick={() => handleReturnSale(sale)}
                              title="Create Return"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-800 transition-colors p-1 rounded"
                              onClick={() => handleDeleteSale(sale)}
                              title="Delete Sale"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Grid View */
          <div>
            {/* Grid View Select All */}
            <div className={`flex items-center justify-between mb-4 p-3 ${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
            } border rounded-lg`}>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center space-x-2 hover:text-blue-600 transition-colors touch-manipulation"
                >
                  {selectedSales.length === filteredSales.length && filteredSales.length > 0 ? (
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                  <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Select All ({filteredSales.length} items)
                  </span>
                </button>
              </div>
              {selectedSales.length > 0 && (
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {selectedSales.length} selected
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {loading ? (
              <>
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className={`${
                      theme === 'dark' ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'
                    } border rounded-lg overflow-hidden animate-pulse`}
                  >
                    <div className="p-4">
                      {/* Header: Checkbox + Sale ID + Status Badge */}
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-start space-x-3 flex-1">
                          {/* Checkbox skeleton */}
                          <div className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          <div className="flex-1 min-w-0">
                            {/* Sale ID skeleton */}
                            <div className={`h-5 w-24 rounded mb-1 ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                            {/* Invoice number skeleton */}
                            <div className={`h-3 w-16 rounded ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                          </div>
                        </div>
                        {/* Status badge skeleton */}
                        <div className={`h-5 w-14 rounded-full flex-shrink-0 ml-2 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>

                      {/* Customer row with icon skeleton */}
                      <div className="flex items-center mb-2">
                        <div className={`w-4 h-4 rounded flex-shrink-0 mr-2 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-20 rounded flex-1 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>

                      {/* Date row with icon skeleton */}
                      <div className="flex items-center mb-2">
                        <div className={`w-4 h-4 rounded flex-shrink-0 mr-2 ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-24 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>

                      {/* Amount + Payment skeleton */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <div className={`w-4 h-4 rounded flex-shrink-0 mr-2 ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          <div className={`h-5 w-20 rounded ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                        <div className={`h-5 w-16 rounded-full ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>

                      {/* Action buttons skeleton */}
                      <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className={`h-4 w-12 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-12 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        <div className={`h-4 w-16 rounded ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : filteredSales.length === 0 ? (
              <div className="col-span-full flex flex-col items-center py-12">
                <FileText className={`w-12 h-12 mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                <p className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  No sales found
                </p>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {filters.search ? 'Try adjusting your search or filters' : 'No sales data available'}
                </p>
              </div>
            ) : (
              filteredSales.map((sale) => (
                <div
                  key={sale.id}
                  className={`${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  } border rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 overflow-hidden ${
                    selectedSales.includes(sale.id) ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-start space-x-3">
                        <button
                          onClick={() => handleSelectSale(sale.id)}
                          className="flex items-center space-x-1 hover:text-blue-600 transition-colors mt-1 touch-manipulation"
                        >
                          {selectedSales.includes(sale.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                        <div>
                          <h3 className={`text-base sm:text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Sale #{sale.shop_display_id || sale.id}
                          </h3>
                          {sale.invoice_number && (
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              {sale.invoice_number}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        {(sale as any).productReturns && (sale as any).productReturns.length > 0 && (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300`}>
                            Returned
                          </span>
                        )}
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          getPaymentStatusBadge((sale as any).display_status || sale.payment_status)
                        }`}>
                          {formatStatusText((sale as any).display_status || sale.payment_status)}
                        </span>
                      </div>
                    </div>

                    {/* Customer */}
                    <div className="flex items-center mb-2">
                      <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} truncate`}>
                        {sale.customer?.name || sale.customer_name || 'Walk-in Customer'}
                      </span>
                    </div>

                    {/* Date */}
                    <div className="flex items-center mb-2">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {new Date(sale.created_at || '').toLocaleDateString('en-PK')}
                      </span>
                    </div>

                    {/* Amount */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(Number(sale.total_amount))}
                        </span>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        getPaymentMethodBadge(sale.payment_method)
                      }`}>
                        {formatPaymentMethod(sale.payment_method)}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => viewSale(sale)}
                        className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors touch-manipulation"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">View</span>
                      </button>
                      <button
                        onClick={() => editSale(sale)}
                        className="flex items-center text-yellow-600 hover:text-yellow-800 text-sm font-medium transition-colors touch-manipulation"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        onClick={() => handleReturnSale(sale)}
                        className="flex items-center text-orange-600 hover:text-orange-800 text-sm font-medium transition-colors touch-manipulation"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Return</span>
                      </button>
                      <button
                        onClick={() => handleDeleteSale(sale)}
                        className="flex items-center text-red-600 hover:text-red-800 text-sm font-medium transition-colors touch-manipulation"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
            </div>
          </div>
        )}

        {/* View Modal - Modern Design */}
        {(showViewModal || isClosingViewModal) && selectedSale
          ? createPortal(
              <div 
                className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] ${
              isClosingViewModal ? 'animate-fadeOut' : 'animate-fadeIn'
            }`}
            onClick={handleCloseViewModal}
          >
            <div 
              className={`w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${
                isClosingViewModal ? 'animate-scaleOut' : 'animate-scaleIn'
              } ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 min-w-0 flex-1">
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                      <Package className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                        Sale Details
                      </h2>
                      <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {selectedSale.invoice_number || `Sale #${selectedSale.shop_display_id || selectedSale.id}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseViewModal}
                    className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                      theme === 'dark' 
                        ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="p-4 sm:p-5 md:p-6">
                {viewModalLoading ? (
                  // Loading Skeleton
                  <div className="space-y-4">
                    {/* Title Skeleton */}
                    <div className="space-y-2">
                      <div className={`h-6 w-48 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                      <div className={`h-4 w-32 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                    </div>

                    {/* Info Boxes Skeleton */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[...Array(2)].map((_, idx) => (
                        <div key={idx} className={`p-4 rounded-lg space-y-2 ${
                          theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                        }`}>
                          <div className={`h-4 w-24 rounded-lg animate-pulse ${
                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                          }`} />
                          {[...Array(3)].map((_, i) => (
                            <div key={i} className={`h-4 w-40 rounded-lg animate-pulse ${
                              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                            }`} />
                          ))}
                        </div>
                      ))}
                    </div>

                    {/* Sale Items Skeleton */}
                    <div className="space-y-3">
                      <div className={`h-5 w-32 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                      {[...Array(3)].map((_, idx) => (
                        <div key={idx} className={`p-3 rounded-lg space-y-2 ${
                          theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                        }`}>
                          <div className={`h-4 w-full rounded-lg animate-pulse ${
                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                          }`} />
                          <div className={`h-4 w-32 rounded-lg animate-pulse ${
                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                          }`} />
                        </div>
                      ))}
                    </div>

                    {/* Payment Summary Skeleton */}
                    <div className={`p-4 rounded-lg space-y-3 ${
                      theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                    }`}>
                      <div className={`h-5 w-40 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                      {[...Array(6)].map((_, idx) => (
                        <div key={idx} className={`h-4 w-full rounded-lg animate-pulse ${
                          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                        }`} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                {/* Customer & Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3 md:gap-4 mb-3 sm:mb-4">
                  <div className={`p-2.5 sm:p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-750' : 'bg-gray-50'}`}>
                    <h4 className={`font-medium mb-2 flex items-center text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      <User className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                      Customer Information
                    </h4>
                    <div className="space-y-2">
                      <div>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Name</p>
                        <p className={`font-medium text-sm break-words ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {selectedSale.customer?.name || selectedSale.customer_name || 'Walk-in Customer'}
                        </p>
                      </div>
                      {selectedSale.customer?.phone && (
                        <div>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Phone</p>
                          <p className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {selectedSale.customer.phone}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Amount</p>
                        <p className={`text-xl sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(Number(selectedSale.total_amount))}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-750' : 'bg-gray-50'}`}>
                    <h4 className={`font-medium mb-3 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      <Calendar className="w-4 h-4 mr-2" />
                      Sale Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Date</p>
                        <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {new Date(selectedSale.created_at || '').toLocaleString('en-PK')}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Payment Method</p>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            getPaymentMethodBadge(selectedSale.payment_method)
                          }`}>
                            {formatPaymentMethod(selectedSale.payment_method)}
                          </span>
                        </div>
                        <div>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Status</p>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            getPaymentStatusBadge((selectedSale as any).display_status || selectedSale.payment_status)
                          }`}>
                            {formatStatusText((selectedSale as any).display_status || selectedSale.payment_status)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Return Information Section */}
                {(() => {
                  const hasReturns = (selectedSale as any).productReturns && (selectedSale as any).productReturns.length > 0;
                  console.log('Return section check - productReturns:', (selectedSale as any).productReturns, 'hasReturns:', hasReturns);
                  return hasReturns && (
                    <div className={`mb-6 space-y-4`}>
                      {(selectedSale as any).productReturns.map((ret: any, idx: number) => (
                        <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                          theme === 'dark'
                            ? 'bg-indigo-900/20 border-indigo-500 border'
                            : 'bg-indigo-50 border-indigo-500'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold flex items-center ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-700'}`}>
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Return Information
                            </h4>
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                              ret.status === 'approved'
                                ? theme === 'dark'
                                  ? 'bg-green-900/50 text-green-300'
                                  : 'bg-green-100 text-green-800'
                                : ret.status === 'rejected'
                                ? theme === 'dark'
                                  ? 'bg-red-900/50 text-red-300'
                                  : 'bg-red-100 text-red-800'
                                : ret.status === 'processed'
                                ? theme === 'dark'
                                  ? 'bg-blue-900/50 text-blue-300'
                                  : 'bg-blue-100 text-blue-800'
                                : theme === 'dark'
                                ? 'bg-yellow-900/50 text-yellow-300'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {ret.status?.charAt(0).toUpperCase() + ret.status?.slice(1)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Return #</p>
                              <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {ret.return_number}
                              </p>
                            </div>
                            <div>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Return Reason</p>
                              <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {ret.reason?.charAt(0).toUpperCase() + ret.reason?.slice(1)}
                              </p>
                            </div>
                            <div>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Refund Amount</p>
                              <p className={`font-semibold text-green-600 ${theme === 'dark' ? 'text-green-400' : ''}`}>
                                {formatCurrency(ret.total_returned_amount || 0)}
                              </p>
                            </div>
                            <div>
                              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Returned Date</p>
                              <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {new Date(ret.returned_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Sale Items */}
                <div className="mb-6">
                  <h4 className={`font-medium mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    <Package className="w-4 h-4 mr-2" />
                    Sale Items ({selectedSale.saleItems?.length || 0})
                  </h4>
                  {selectedSale.saleItems && selectedSale.saleItems.length > 0 ? (
                    <>
                      {/* Mobile Card View */}
                      <div className="block lg:hidden space-y-3">
                        {selectedSale.saleItems.map((item, index) => (
                          <div key={index} className={`p-3 rounded-lg border ${
                            theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                          }`}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <div className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                  {item.product?.name || item.product_name}
                                </div>
                                {item.product?.brand && (
                                  <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {item.product.brand}
                                  </div>
                                )}
                              </div>
                              <div className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {formatCurrency(Number(item.total_price))}
                              </div>
                            </div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                Qty: {item.quantity} {(item.unit || item.product?.unit || 'piece') && <span className="font-medium">{item.unit || item.product?.unit || 'piece'}</span>}
                              </span>
                              <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                Unit: {formatCurrency(Number(item.unit_price))}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop Table View */}
                      <div className={`hidden lg:block max-h-64 overflow-y-auto border rounded-lg ${
                        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                      }`}>
                        {/* Header */}
                        <div className={`grid grid-cols-12 gap-2 p-3 text-sm font-medium border-b ${
                          theme === 'dark' ? 'border-gray-700 bg-gray-750 text-gray-300' : 'border-gray-200 bg-gray-100 text-gray-600'
                        }`}>
                          <div className="col-span-4">Product</div>
                          <div className="col-span-2 text-center">Qty</div>
                          <div className="col-span-2 text-center">Unit</div>
                          <div className="col-span-2 text-right">Unit Price</div>
                          <div className="col-span-2 text-right">Total</div>
                        </div>
                        
                        {/* Items */}
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {selectedSale.saleItems.map((item, index) => (
                            <div key={index} className={`grid grid-cols-12 gap-2 p-3 hover:bg-opacity-50 transition-colors ${
                              theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                            }`}>
                              <div className="col-span-4">
                                <div className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                  {item.product?.name || item.product_name}
                                </div>
                                {item.product?.brand && (
                                  <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {item.product.brand}
                                  </div>
                                )}
                              </div>
                              <div className={`col-span-2 text-center text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {item.quantity}
                              </div>
                              <div className={`col-span-2 text-center text-sm font-medium ${
                                theme === 'dark' 
                                  ? 'text-indigo-400' 
                                  : 'text-indigo-600'
                              }`}>
                                {item.unit || item.product?.unit || 'piece'}
                              </div>
                              <div className={`col-span-2 text-right text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {formatCurrency(Number(item.unit_price))}
                              </div>
                              <div className={`col-span-2 text-right font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {formatCurrency(Number(item.total_price))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className={`p-6 text-center border rounded-lg ${
                      theme === 'dark' ? 'border-gray-700 bg-gray-750 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-500'
                    }`}>
                      <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No items found for this sale</p>
                    </div>
                  )}
                </div>

                {/* Payment Summary */}
                <div className={`p-4 sm:p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-750 border-gray-700' : 'bg-gray-50 border-gray-200'} border`}>
                  <h4 className={`font-medium mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Payment Summary
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Subtotal:</span>
                      <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                        {formatCurrency(Number(selectedSale.subtotal || selectedSale.total_amount))}
                      </span>
                    </div>
                    {Number(selectedSale.discount_amount || 0) > 0 && (
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Discount:</span>
                        <span className="text-red-600">-{formatCurrency(Number(selectedSale.discount_amount))}</span>
                      </div>
                    )}
                    {Number(selectedSale.tax_amount) > 0 && (
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Tax:</span>
                        <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                          {formatCurrency(Number(selectedSale.tax_amount))}
                        </span>
                      </div>
                    )}
                    <div className={`pt-2 border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                      <div className="flex justify-between text-base sm:text-lg font-semibold">
                        <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Total:</span>
                        <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                          {formatCurrency(Number(selectedSale.total_amount))}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Paid:</span>
                      <span className="text-green-600">{formatCurrency(Number(selectedSale.paid_amount))}</span>
                    </div>
                    {Number(selectedSale.due_amount) > 0 && (
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Due:</span>
                        <span className="text-red-600">{formatCurrency(Number(selectedSale.due_amount))}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                {selectedSale.notes && (
                  <div className="mt-6">
                    <h4 className={`font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Notes
                    </h4>
                    <p className={`text-sm p-3 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-750 text-gray-300' : 'bg-gray-50 text-gray-700'
                    }`}>
                      {selectedSale.notes}
                    </p>
                  </div>
                )}
                  </>
                )}
              </div>
              </div>

              {/* Footer */}
              <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${
                theme === 'dark' ? 'border-gray-700 bg-gray-800/95' : 'border-gray-200 bg-gray-50/95'
              }`}>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleCloseViewModal}
                    className={`px-6 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation ${
                      theme === 'dark'
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
        : null
        }

        {/* Edit Modal - Product Page Style */}
        {(showEditModal || isClosingEditModal) && selectedSale && (
          <div className={`fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50 ${
            isClosingEditModal ? 'animate-fadeOut' : 'animate-fadeIn'
          }`}>
            <div className={`max-w-3xl w-full rounded-xl shadow-2xl max-h-[90vh] flex flex-col ${
              isClosingEditModal ? 'animate-scaleOut' : 'animate-scaleIn'
            } ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
              
              {/* Header */}
              <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${
                theme === 'dark' ? 'border-gray-700 bg-gray-800/95' : 'border-gray-200 bg-gray-50/95'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 min-w-0 flex-1">
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                      theme === 'dark' ? 'bg-yellow-900/50' : 'bg-yellow-100'
                    }`}>
                      <Edit className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                        theme === 'dark' ? 'text-yellow-300' : 'text-yellow-600'
                      }`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                        theme === 'dark' ? 'text-white' : 'text-slate-900'
                      }`}>
                        Edit Sale
                      </h2>
                      <p className={`text-xs truncate ${
                        theme === 'dark' ? 'text-gray-400' : 'text-slate-500'
                      }`}>
                        Update sale information - {selectedSale.invoice_number || `Sale #${selectedSale.shop_display_id || selectedSale.id}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseEditModal}
                    className={`p-2 rounded-lg transition-colors flex-shrink-0 touch-manipulation ${
                      theme === 'dark' 
                        ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                        : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Scrollable Form Content */}
              <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              {editModalLoading ? (
                // Loading Skeleton
                <form className="p-4 sm:p-5 md:p-6">
                  <div className="space-y-4 sm:space-y-5">
                    {/* Customer Section Skeleton */}
                    <div className="space-y-3">
                      <div className={`h-5 w-32 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                      <div className={`h-10 w-full rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                    </div>

                    {/* Payment Details Section Skeleton */}
                    <div className="space-y-3">
                      <div className={`h-5 w-32 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[...Array(2)].map((_, idx) => (
                          <div key={idx} className="space-y-2">
                            <div className={`h-4 w-24 rounded-lg animate-pulse ${
                              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                            }`} />
                            <div className={`h-10 w-full rounded-lg animate-pulse ${
                              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                            }`} />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notes Section Skeleton */}
                    <div className="space-y-3">
                      <div className={`h-5 w-32 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                      <div className={`h-24 w-full rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                    </div>
                  </div>
                </form>
              ) : (
              <form onSubmit={(e) => { e.preventDefault(); updateSale(); }} id="edit-sale-form" className="p-4 sm:p-5 md:p-6">
                <div className="space-y-4 sm:space-y-5">
                  
                  {/* Customer Information */}
                  <div className="space-y-2.5 sm:space-y-3">
                    <h3 className={`text-sm sm:text-base font-medium flex items-center space-x-2 ${
                      theme === 'dark' ? 'text-white' : 'text-slate-900'
                    }`}>
                      <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span>Customer Information</span>
                    </h3>
                    
                    <div>
                      <label htmlFor="customer_name" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                      }`}>
                        Customer Name *
                      </label>
                      <input
                        type="text"
                        id="customer_name"
                        value={editForm.customer_name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, customer_name: e.target.value }))}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 touch-manipulation ${
                          theme === 'dark' 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="Enter customer name"
                        required
                      />
                    </div>
                  </div>

                  {/* Payment Information */}
                  <div className="space-y-3 sm:space-y-4">
                    <h3 className={`text-sm sm:text-base md:text-lg font-medium flex items-center space-x-2 ${
                      theme === 'dark' ? 'text-white' : 'text-slate-900'
                    }`}>
                      <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>Payment Details</span>
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      {/* Paid Amount */}
                      <div>
                        <label htmlFor="paid_amount" className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                        }`}>
                          Paid Amount (PKR) *
                        </label>
                        <input
                          type="number"
                          id="paid_amount"
                          min="0"
                          step="0.01"
                          value={editForm.paid_amount}
                          onChange={(e) => {
                            const newPaidAmount = Number(e.target.value);
                            const totalAmount = Number(selectedSale?.total_amount || 0);
                            const newStatus = calculatePaymentStatus(newPaidAmount, totalAmount);
                            setEditForm(prev => ({ ...prev, paid_amount: newPaidAmount, payment_status: newStatus }));
                          }}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                            theme === 'dark' 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                          placeholder="0.00"
                          required
                        />
                        <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-slate-500'}`}>
                          Total Amount: {formatCurrency(Number(selectedSale.total_amount))}
                        </p>
                      </div>

                      {/* Payment Method */}
                      <div>
                        <label htmlFor="payment_method" className={`block text-sm font-medium mb-2 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                        }`}>
                          Payment Method *
                        </label>
                        <select
                          id="payment_method"
                          value={editForm.payment_method}
                          onChange={(e) => setEditForm(prev => ({ ...prev, payment_method: e.target.value as 'cash' | 'bank_transfer' | 'other' }))}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                            theme === 'dark' 
                              ? 'bg-gray-700 border-gray-600 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        >
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    {/* Payment Status */}
                    <div>
                      <label htmlFor="payment_status" className={`block text-sm font-medium mb-2 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                      }`}>
                        Payment Status (Auto-Calculated)
                      </label>
                      <div className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-all duration-200 ${
                        theme === 'dark' 
                          ? 'bg-gray-800 border-gray-600 text-gray-300' 
                          : 'bg-gray-100 border-gray-300 text-gray-600'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium capitalize">
                            {editForm.payment_status === 'paid' && '💰 Paid'}
                            {editForm.payment_status === 'partial' && '⏳ Partial'}
                            {editForm.payment_status === 'due' && '📋 Due'}
                          </span>
                          <span className="text-xs opacity-75">Based on paid amount</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-4">
                    <h3 className={`text-lg font-medium flex items-center space-x-2 ${
                      theme === 'dark' ? 'text-white' : 'text-slate-900'
                    }`}>
                      <FileText className="w-5 h-5" />
                      <span>Additional Information</span>
                    </h3>
                    
                    <div>
                      <label htmlFor="notes" className={`block text-sm font-medium mb-2 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                      }`}>
                        Notes (Optional)
                      </label>
                      <textarea
                        id="notes"
                        value={editForm.notes}
                        onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none ${
                          theme === 'dark' 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="Add any additional notes about this sale..."
                      />
                    </div>
                  </div>
                </div>
              </form>
              )}
              </div>

              {/* Footer */}
              <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${
                theme === 'dark' ? 'border-gray-700 bg-gray-800/95' : 'border-gray-200 bg-gray-50/95'
              }`}>
                <div className="flex flex-col-reverse sm:flex-row gap-3">
                  {editModalLoading ? (
                    <>
                      <div className={`w-full sm:flex-1 h-11 rounded-xl animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                      <div className={`w-full sm:flex-1 h-11 rounded-xl animate-pulse ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                      }`} />
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleCloseEditModal}
                        className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${
                          theme === 'dark' 
                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' 
                            : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        form="edit-sale-form"
                        className="w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 touch-manipulation bg-blue-500 hover:bg-blue-600 text-white"
                      >
                        <Check className="w-4 h-4" />
                        <span>Update Sale</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          title={confirmationModal.title}
          message={confirmationModal.message}
          onConfirm={confirmationModal.onConfirm}
          onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
          type={confirmationModal.type}
          isLoading={deleteLoading}
        />

        {/* Return Request Modal */}
        <ReturnRequestModal
          sale={selectedSale}
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
          onSuccess={handleReturnSuccess}
        />
    </div>
  );
};

export default SalesManagementPage;
